import { isEmpty, isNil, keyBy } from 'lodash';
import { DateTime } from 'luxon';

import type {
  CounselorMeritBadge,
  MeritBadge,
  MeritBadgeCounselorProfile,
} from '@appTypes/esb';
import { useMBDict } from '@dict/duck/hooks';
import { getPreferredName } from '@shared/utils/preferredName';

import { useGetMeritBadgeCounselorProfileQuery } from './api';

export type CounselorMeritBadgeExt = CounselorMeritBadge & {
  image: string;
  imageUrl100: string;
};

function mapMeritBadgesExtraInfo({
  records,
  detailsLookup,
  filterExpired,
}: {
  records: CounselorMeritBadge[];
  detailsLookup: Record<number, MeritBadge>;
  filterExpired: boolean;
}): CounselorMeritBadgeExt[] {
  return records
    .map(record => {
      const detail = detailsLookup[record.meritBadgeId];
      return {
        ...record,
        image: detail?.image ?? '',
        imageUrl100: detail?.imageUrl100 ?? '',
      };
    })
    .filter(record => {
      if (!filterExpired || isEmpty(record.expiryDt)) {
        return true;
      }

      return (
        DateTime.fromISO(record.expiryDt ?? '') >= DateTime.now().startOf('day')
      );
    })
    .sort((a, b) => a.meritBadgeName?.localeCompare(b.meritBadgeName));
}

/** Used in the widget for the profile */
export const useMeritBadgeCounselorProfile = ({
  memberId,
  filterExpiredMbs = true,
  extendedInfo = false,
}: {
  memberId: string | null;
  filterExpiredMbs?: boolean;
  extendedInfo?: boolean;
}) => {
  const { data, isFetching: loadingMbcProfile } =
    useGetMeritBadgeCounselorProfileQuery(
      {
        memberId: memberId ?? -1,
      },
      { skip: isNil(memberId) },
    );

  const { meritBadges: mbsLookup } = useMBDict({
    skip: !extendedInfo,
  });

  const mbsLookupDict = keyBy(mbsLookup, 'id');

  if (isNil(data)) {
    return {
      name: '',
      hasValidRegistration: false,
      availability: null,
      meritBadges: [] as CounselorMeritBadgeExt[],
      counseledYouths: [] as MeritBadgeCounselorProfile['youths'],
      loading: loadingMbcProfile,
    };
  }

  return {
    name: getPreferredName(data),
    hasValidRegistration: data.hasValidRegistration,
    availability: {
      id: data.counselorAvailabilityId,
      short: data.counselorAvailabilityShort,
      name: data.counselorAvailabilityLong,
    },
    meritBadges: mapMeritBadgesExtraInfo({
      records: data?.meritBadges ?? [],
      detailsLookup: mbsLookupDict,
      filterExpired: filterExpiredMbs,
    }),
    counseledYouths: data.youths,
    loading: loadingMbcProfile,
  };
};
